import { baseApi } from "../api/baseAPI";

export type ChList = {
  name: string;
  channel_url: string;
  is_public: string;
};

export interface IPromptsType {
  question: string;
  answer: string;
  questionId: string;
}
export interface IGetProfileDetailsResponse {
  _id: string;
  onboardingDate: Date;
  headline: string;
  image: string;
  name: string;
  industryTags: string[];
  city: string;
  connected: boolean;
  connectionsCount: number;
  goals: string[];
  experience: string;
  leader: string;
  currentCompany: string;
  companyAdmire: string[];
  undergrad: string;
  postgrad: string;
  topSkills: string[];
  hobbies: string[];
  introduction: string;
  featuredLinks: [];
  chList: ChList[];
  previousWorkExperience: any[];
  prompts?: IPromptsType[];
  promptNudgeCount?: number;
  promptNudgeStatus?: boolean;
  isVip: boolean;
  champion: string;
  isBookmarked: boolean;
}

interface UpdateCoreFieldsRequest {
  name: string;
  headline: string;
  city: string;
  currentCompany: string;
}
interface UpdateProfilePicRequest {
  profilePicKey: string;
}
interface UpdateEducationRequest {
  undergrad: string;
  postgrad: string;
}
interface AddPreviousExperienceRequest {
  company: string;
  designation: string;
  brief?: string;
  startDate?: Date | null;
  endDate?: Date | null;
}
interface EditPreviousExperienceRequest {
  previousWorkExperienceId: string;
  company: string;
  designation: string;
  brief?: string;
  startDate?: Date | null;
  endDate?: Date | null;
}
interface AllScRequest {
  page: number;
  otherUserId: string;
}
interface UpdatePromptAnswerRequest {
  questionId: string;
  text: string;
}
interface DeletePromptAnswerRequest {
  questionId: string;
}
interface PromptNudgeRequest {
  otherUserId: string;
}
interface UpForRenewalResponse {
  ufrDate?: string;
  paymentLink?: string;
}

interface ProfileStatsResponse {
  followersInLast30days: number;
  postImpressions: number;
  profilePageVisitsInLast30days: number;
  totalFollowers: number;
  totalFollowing: number;
}
interface BannerResponse {
  startColor: string;
  endColor: string;
  title: string;
  subTitle: string;
  helperText?: string;
  route: string;
  params?: any;
  trackingReference: string;
}

const profileApiWithTags = baseApi.enhanceEndpoints({
  addTagTypes: ["GET_PROFILE_DETAILS", "GET_PROMPTS_LIST"],
});

export const profileApi = profileApiWithTags.injectEndpoints({
  endpoints: (builder) => ({
    getProfileDetails: builder.query<IGetProfileDetailsResponse, string>({
      query: (id) => ({
        url: `/v1/profile/details/${id}`,
        method: "GET",
      }),
      providesTags: ["GET_PROFILE_DETAILS"],
    }),
    updateCoreFields: builder.mutation<any, UpdateCoreFieldsRequest>({
      query: (fields) => ({
        url: "/v1/profile/updateCoreFields",
        method: "POST",
        data: fields,
      }),
      invalidatesTags: ["GET_PROFILE_DETAILS"],
    }),
    updateProfilePic: builder.mutation<any, UpdateProfilePicRequest>({
      query: (fields) => ({
        url: "/v1/profile/updateProfilePic",
        method: "POST",
        data: fields,
      }),
      invalidatesTags: ["GET_PROFILE_DETAILS"],
    }),
    updateEducation: builder.mutation<any, UpdateEducationRequest>({
      query: (fields) => ({
        url: "/v1/profile/updateEducation",
        method: "POST",
        data: fields,
      }),
      invalidatesTags: ["GET_PROFILE_DETAILS"],
    }),
    updateExperience: builder.mutation<any, string>({
      query: (experience) => ({
        url: "/v1/profile/updateExperience",
        method: "POST",
        data: { experience },
      }),
      invalidatesTags: ["GET_PROFILE_DETAILS"],
    }),
    updateLeader: builder.mutation<any, string>({
      query: (leader) => ({
        url: "/v1/profile/updateLeader",
        method: "POST",
        data: { leader },
      }),
      invalidatesTags: ["GET_PROFILE_DETAILS"],
    }),
    updateIntroduction: builder.mutation<any, string>({
      query: (introduction) => ({
        url: "/v1/profile/updateIntroduction",
        method: "POST",
        data: { introduction },
      }),
      invalidatesTags: ["GET_PROFILE_DETAILS"],
    }),
    updateGoals: builder.mutation<any, string[]>({
      query: (goals) => ({
        url: "/v1/profile/updateGoals",
        method: "POST",
        data: { goals },
      }),
      invalidatesTags: ["GET_PROFILE_DETAILS"],
    }),
    updateAdmiredCompanies: builder.mutation<any, string[]>({
      query: (companyAdmire) => ({
        url: "/v1/profile/updateCompaniesAdmired",
        method: "POST",
        data: { companyAdmire },
      }),
      invalidatesTags: ["GET_PROFILE_DETAILS"],
    }),
    updateHobbies: builder.mutation<any, string[]>({
      query: (hobbies) => ({
        url: "/v1/profile/updateHobbies",
        method: "POST",
        data: { hobbies },
      }),
      invalidatesTags: ["GET_PROFILE_DETAILS"],
    }),
    updateTopSkills: builder.mutation<any, string[]>({
      query: (topSkills) => ({
        url: "/v1/profile/updateTopSkills",
        method: "POST",
        data: { topSkills },
      }),
      invalidatesTags: ["GET_PROFILE_DETAILS"],
    }),
    updateFeaturedLinks: builder.mutation<any, string[]>({
      query: (featuredLinks) => ({
        url: "/v1/profile/updateFeaturedLinks",
        method: "POST",
        data: { featuredLinks },
      }),
      invalidatesTags: ["GET_PROFILE_DETAILS"],
    }),
    updateIndustryTags: builder.mutation<any, string[]>({
      query: (directories) => ({
        url: "/v1/profile/updateDirectories",
        method: "POST",
        data: { directories },
      }),
      invalidatesTags: ["GET_PROFILE_DETAILS"],
    }),
    addPreviousExperience: builder.mutation<any, AddPreviousExperienceRequest>({
      query: (details) => ({
        url: "/v1/profile/createPreviousWorkExperience",
        method: "POST",
        data: details,
      }),
      invalidatesTags: ["GET_PROFILE_DETAILS"],
    }),
    editPreviousExperience: builder.mutation<
      any,
      EditPreviousExperienceRequest
    >({
      query: (details) => ({
        url: "/v1/profile/editPreviousWorkExperience",
        method: "POST",
        data: details,
      }),
      invalidatesTags: ["GET_PROFILE_DETAILS"],
    }),
    deletePreviousExperience: builder.mutation<any, string>({
      query: (previousWorkExperienceId) => ({
        url: "/v1/profile/deletePreviousWorkExperience",
        method: "POST",
        data: { previousWorkExperienceId },
      }),
      invalidatesTags: ["GET_PROFILE_DETAILS"],
    }),
    getAllSuperConnections: builder.query<
      IGetProfileDetailsResponse,
      AllScRequest
    >({
      query: ({ page, otherUserId }) => ({
        url: `/v1/connection/listAll?page=${page}`,
        method: "POST",
        data: { otherUserId },
      }),
    }),
    getPromptsList: builder.query<IPromptsType[], void>({
      query: () => ({
        url: "/v1/profile/prompts/list",
        method: "GET",
      }),
      providesTags: ["GET_PROMPTS_LIST"],
    }),
    updatePromptAnswer: builder.mutation<any, UpdatePromptAnswerRequest>({
      query: (data) => ({
        url: "/v1/profile/prompts/updateAnswer",
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["GET_PROFILE_DETAILS", "GET_PROMPTS_LIST"],
    }),
    deletePromptAnswer: builder.mutation<any, DeletePromptAnswerRequest>({
      query: (data) => ({
        url: "/v1/profile/prompts/deleteAnswer",
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["GET_PROFILE_DETAILS", "GET_PROMPTS_LIST"],
    }),
    createPromptNudge: builder.mutation<any, PromptNudgeRequest>({
      query: (data) => ({
        url: "/v1/profile/prompts/nudge",
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["GET_PROFILE_DETAILS"],
    }),
    getUpForRenewalDate: builder.query<UpForRenewalResponse, void>({
      query: () => ({
        url: "/v1/profile/ufrDate",
        method: "GET",
      }),
    }),
    bookmarkProfile: builder.mutation<any, string>({
      query: (profileId: string) => ({
        url: `/v1/profile/bookmark?profileId=${profileId}`,
        method: "POST",
      }),
      invalidatesTags: ["GET_PROFILE_DETAILS"],
    }),
    registerProfileVisit: builder.query<
      any,
      { userId: string; profileId: string }
    >({
      query: ({ userId, profileId }) => ({
        url: `/v1/profile/registerVisit?userId=${userId}&profileId=${profileId}`,
        method: "GET",
      }),
    }),
    getProfileStats: builder.query<ProfileStatsResponse, string>({
      query: (userId: string) => ({
        url: `/v1/profile/stats?userId=${userId}`,
        method: "GET",
      }),
    }),
    getBanners: builder.query<BannerResponse, void>({
      query: () => ({
        url: "/v2/banners",
        method: "GET",
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetProfileDetailsQuery,
  useUpdateCoreFieldsMutation,
  useUpdateProfilePicMutation,
  useUpdateEducationMutation,
  useUpdateExperienceMutation,
  useUpdateIntroductionMutation,
  useUpdateLeaderMutation,
  useUpdateGoalsMutation,
  useUpdateAdmiredCompaniesMutation,
  useUpdateTopSkillsMutation,
  useUpdateHobbiesMutation,
  useUpdateFeaturedLinksMutation,
  useUpdateIndustryTagsMutation,
  useAddPreviousExperienceMutation,
  useEditPreviousExperienceMutation,
  useDeletePreviousExperienceMutation,
  useGetAllSuperConnectionsQuery,
  useGetPromptsListQuery,
  useUpdatePromptAnswerMutation,
  useDeletePromptAnswerMutation,
  useCreatePromptNudgeMutation,
  useGetUpForRenewalDateQuery,
  useGetBannersQuery,
  useGetProfileStatsQuery,
  useRegisterProfileVisitQuery,
  useBookmarkProfileMutation,
} = profileApi;
